/* ==========================================================================
   Formatting rules, in one place.
   --------------------------------------------------------------------------
   Everything a robot produces is rendered by these, so a battery percentage
   reads the same in a table cell, a header meter and a tooltip. Nothing here
   invents a value: a missing reading formats as an em dash, never as zero.
   ========================================================================== */

/** The one glyph for "we do not have this value". */
export const NO_VALUE = '—';

/**
 * Does this unit sit hard against its number?
 *
 * `%` and `°` are typographic suffixes of the numeral — `79.9%`, `28.0°` —
 * while every other unit in this console is a word or an abbreviation and
 * takes a thin space: `0.46 m/s`, `39.2 °C`, `81.5 GiB`, `114 ms`. One rule,
 * applied by every primitive that prints a unit, so the same reading is set
 * the same way in a meter readout, a table cell, a KPI and a spec row.
 */
export function unitIsTight(unit: string): boolean {
  return unit === '%' || unit === '°';
}

export function pct(v: number | null | undefined, digits = 0): string {
  if (v == null || Number.isNaN(v)) return NO_VALUE;
  return `${v.toFixed(digits)}%`;
}

export function num(
  v: number | null | undefined,
  digits = 0,
): string {
  if (v == null || Number.isNaN(v)) return NO_VALUE;
  return v.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function bytes(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return NO_VALUE;
  if (v < 1024) return `${v} B`;
  const units = ['KiB', 'MiB', 'GiB', 'TiB'];
  let n = v / 1024;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n < 10 ? 1 : 0)} ${units[i]}`;
}

/**
 * The console's ONE elapsed-time formatter. Two forms, one rule.
 *
 *   duration(ms)                  4s · 12m 30s · 3h 05m · 2d 4h · 205d
 *   duration(ms, { compact: 1 })  4s ·     12m ·     3h ·    2d · 205d
 *
 * Two significant units, and past ten days only one. A certificate that
 * expires in seven months is `in 205d`: the trailing `23h` on a figure that
 * size is precision the reading does not have and a column of it is 40px of
 * noise on every row. Inside ten days the second unit is back, because that
 * is where the difference between `8d 23h` and `8d 02h` is a working day.
 *
 * `compact: 1` drops to a single unit and never exceeds four characters. It
 * is for the places that genuinely have no room for the second one — the map
 * pin riding beside a 19px symbol, the top bar's fixed age slot, a panel
 * header's freshness mark, the fleet table's `Last seen` column. It is NOT a
 * second opinion about the number: both forms TRUNCATE at every boundary, so
 * a reading that is 7h 39m old is `7h 39m` where there is room and `7h` where
 * there is not — never `8h`. Rounding at the top unit is how the console used
 * to print `24h`, a value that cannot exist because the next unit is days.
 */
export function duration(
  ms: number | null | undefined,
  opts: { compact?: 1 } = {},
): string {
  if (ms == null || Number.isNaN(ms)) return NO_VALUE;
  const one = opts.compact === 1;
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return one ? `${m}m` : `${m}m ${String(s % 60).padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return one ? `${h}h` : `${h}h ${String(m % 60).padStart(2, '0')}m`;
  const d = Math.floor(h / 24);
  if (one || d >= 10) return `${d}d`;
  return `${d}d ${h % 24}h`;
}

/** "4s ago" / "in 9d" — the form used beside a timestamp. */
export function relative(
  iso: string | null | undefined,
  now = Date.now(),
  opts: { compact?: 1 } = {},
): string {
  if (!iso) return NO_VALUE;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return NO_VALUE;
  const delta = now - t;
  if (Math.abs(delta) < 2000) return 'now';
  return delta >= 0 ? `${duration(delta, opts)} ago` : `in ${duration(-delta, opts)}`;
}

const PAD = (n: number) => String(n).padStart(2, '0');

/** Zulu, always. `2026-08-26 12:51:49Z` — sortable, unambiguous, mono. */
export function utc(iso: string | null | undefined, withSeconds = true): string {
  if (!iso) return NO_VALUE;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const date = `${d.getUTCFullYear()}-${PAD(d.getUTCMonth() + 1)}-${PAD(d.getUTCDate())}`;
  const time = `${PAD(d.getUTCHours())}:${PAD(d.getUTCMinutes())}`;
  return withSeconds ? `${date} ${time}:${PAD(d.getUTCSeconds())}Z` : `${date} ${time}Z`;
}

/** Local wall clock, for the operator standing next to the robot. */
export function local(iso: string | null | undefined): string {
  if (!iso) return NO_VALUE;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

/** Time only, Zulu — for a dense column where the date is in the header. */
export function utcTime(iso: string | null | undefined): string {
  if (!iso) return NO_VALUE;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return `${PAD(d.getUTCHours())}:${PAD(d.getUTCMinutes())}:${PAD(d.getUTCSeconds())}Z`;
}

/**
 * A WGS84 fix, as the operator would read it back over the radio: five
 * decimals (~1.1 m), both signs explicit by the minus, comma-separated, mono.
 * Never rounded to fewer places than the fix has — a coarser coordinate is a
 * different claim about where the robot is.
 */
export function latlon(
  lat: number | null | undefined,
  lon: number | null | undefined,
): string {
  if (lat == null || lon == null || Number.isNaN(lat) || Number.isNaN(lon)) return NO_VALUE;
  return `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
}

/**
 * Middle ellipsis — keeps the discriminating end of a machine string visible.
 * `20260826T1317Z_row-14` → `20260826…_row-14`, never `20260826T131…`.
 */
export function midEllipsis(value: string, max: number): string {
  if (value.length <= max) return value;
  const head = Math.ceil((max - 1) / 2);
  const tail = Math.floor((max - 1) / 2);
  return `${value.slice(0, head)}…${value.slice(value.length - tail)}`;
}
